import React, { useCallback, useEffect } from 'react';

import {
	ConfluencePageTree,
	DraftsToggle,
	type PageActionHandler,
	TreeStates,
	useConfluencePageTree,
	ContentType,
} from '@atlassiansox/confluence-table-tree';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/analytics-bridge';

import mountTrackerFactory from '../../common/analytics/mount-tracker-factory';

import { useProjectPages } from '../../containers/project-pages';

import { PageTreePlaceholder } from './placeholder';
import { DisplayWrapper } from './styles';
import { type Props } from './types';
import { useEmbeddedPage } from '../../containers/embedded-page';
import { useCreateEmbeddedPage } from '../../containers/embedded-page/utils';
import {
	ExperiencePerformanceTypes,
	ExperienceSuccess,
	ExperienceTypes,
	UFOExperience,
	useUFOComponentExperience,
} from '@atlassian/ufo';
import { getAllBooleanFeatureFlagKeys } from '../../common/util/get-boolean-feature-flags';
import { PreselectedTemplate } from './preselected-template';
import { useTemplate } from '../../containers/template';

const ufoExperienceTracker = new UFOExperience('project-pages-embedded-page-tree', {
	performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
	type: ExperienceTypes.Load,
	featureFlags: getAllBooleanFeatureFlagKeys(),
});

const MountTracker = mountTrackerFactory('confluencePageTree');

export const PageTree = ({
	blueprints,
	contentId,
	isConfluenceActivationCompleteForPreselectedTemplate,
	spaceKey,
	readOnly,
	EmbeddedProductUpdater,
	projectId,
	projectType,
	showDraftToggle = true,
}: Props) => {
	useUFOComponentExperience(ufoExperienceTracker);
	const { treeState, setContent, setProjectData } = useConfluencePageTree();
	const { setEmbeddedPage } = useEmbeddedPage();
	const showRealTree = treeState === TreeStates.RenderedChildren;
	const isTreeLoaded = treeState !== TreeStates.Loading && treeState !== null;
	const { cloudId, locale } = useProjectPages();
	const { template } = useTemplate();
	const handleEdit = useCallback<PageActionHandler>(
		(page) => {
			if (!page.hasEmbeddedEdit) {
				window.open(page.editUrl, '_blank');
			} else {
				setEmbeddedPage({
					isOpen: true,
					isLoading: true,
					page,
					mode: page.isLive ? 'view' : 'edit',
				});
			}
		},
		[setEmbeddedPage],
	);
	const handleView = useCallback<PageActionHandler>(
		(page) => {
			if (page.type !== ContentType.PAGE) {
				window.open(page.url, '_blank');
			} else {
				setEmbeddedPage({
					isOpen: true,
					isLoading: true,
					page,
					mode: 'view',
				});
			}
		},
		[setEmbeddedPage],
	);
	const createEmbeddedPage = useCreateEmbeddedPage({
		spaceKey,
		projectId,
		projectType,
	});
	const handleAddChildPage = useCallback(
		async (parentContentId: string) => {
			await createEmbeddedPage(parentContentId, false, undefined, undefined, template?.key);
		},
		[createEmbeddedPage, template],
	);
	useEffect(() => {
		if (spaceKey) {
			setContent(spaceKey, contentId || undefined);
		}
		setProjectData(projectId, projectType);
	}, [contentId, setContent, spaceKey, setProjectData, projectId, projectType]);

	const pageTree = (
		<>
			<ContextualAnalyticsData sourceType={SCREEN} sourceName="projectPages">
				{isTreeLoaded && showDraftToggle && <DraftsToggle locale={locale} />}
				{/* always render the real tree into the DOM, but sometimes hide it */}
				<DisplayWrapper isShown={showRealTree}>
					<ConfluencePageTree
						key={`${spaceKey}${contentId}`} // force this component to remount whenever contentId changes to reset its internal state, otherwise onStateChanged will not be fired
						cloudId={cloudId}
						readOnly={readOnly}
						onEdit={handleEdit}
						onView={handleView}
						onAddChildPage={handleAddChildPage}
						locale={locale}
					/>
				</DisplayWrapper>
				<PageTreePlaceholder treeState={treeState} />
			</ContextualAnalyticsData>
			{showRealTree && <EmbeddedProductUpdater embeddedProduct="confluence" />}
			{isTreeLoaded && <ExperienceSuccess experience={ufoExperienceTracker} />}
			{isTreeLoaded && (
				<PreselectedTemplate
					blueprints={blueprints}
					createEmbeddedPage={createEmbeddedPage}
					contentId={contentId}
					isConfluenceActivationCompleteForPreselectedTemplate={
						isConfluenceActivationCompleteForPreselectedTemplate
					}
				/>
			)}
			<MountTracker />
		</>
	);

	// ConfluencePageTreeProvider includes the AnalyticsProvider already
	return pageTree;
};
